/**
 * useSummaryReplay.ts — Drives replay of pre-recorded visualization data on Cesium
 *
 * Responsibilities:
 *  1. Pre-process visualization API response: sort each element's states ascending,
 *     build a global sorted time index, compute start/end times.
 *  2. On play: step through time indices using requestAnimationFrame,
 *     interpolate element positions at the current time, and feed them
 *     into the same Cesium helpers used by live mode.
 *  3. Support play / pause / seek / speed control.
 */

import { useCallback, useEffect, useRef } from 'react';
import useGenericSimulator from './useGenericSimulator';
import {
    appendEcefHistory,
    cleanupCesiumPlot,
    computeAltitudeKm,
    computeLVLHEulerAngles,
    computeSpeedMs,
    inferElementTypeFromName,
    isViewerReady,
    throttledRequestRender,
    updateEntityFromECEF,
    updateGeometricCommLines,
} from '../helpers/cesiumPlotHelpers';
import {
    ECEFState,
    ElementMap,
    ElementPositionData,
    ReplayState,
    SelectedElementInfo,
    VisualizationStateEntry,
} from '../types';

// ─── Sorted state data per element (pre-processed once) ─────────────
interface PreparedElement {
    elementId: string;
    elementName: string;
    /** States sorted ascending by rel_time_ms */
    states: VisualizationStateEntry[];
    /** Cursor: last index used for binary-search start */
    cursor: number;
}

/**
 * Binary-search for the rightmost entry with rel_time_ms <= targetMs.
 * Returns the index, or -1 if all entries are after targetMs.
 */
const findFrameIndex = (states: VisualizationStateEntry[], targetMs: number): number => {
    let lo = 0;
    let hi = states.length - 1;
    let result = -1;
    while (lo <= hi) {
        const mid = (lo + hi) >>> 1;
        if (states[mid].rel_time_ms <= targetMs) {
            result = mid;
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    return result;
};

/**
 * Lerp between two state entries at the given time.
 */
const interpolateState = (
    a: VisualizationStateEntry,
    b: VisualizationStateEntry,
    timeMs: number,
): VisualizationStateEntry => {
    const dt = b.rel_time_ms - a.rel_time_ms;
    if (dt === 0) return a;
    const t = Math.max(0, Math.min(1, (timeMs - a.rel_time_ms) / dt));
    return {
        timestamp: a.timestamp + (b.timestamp - a.timestamp) * t,
        rel_time_ms: timeMs,
        position: {
            pos_x: a.position.pos_x + (b.position.pos_x - a.position.pos_x) * t,
            pos_y: a.position.pos_y + (b.position.pos_y - a.position.pos_y) * t,
            pos_z: a.position.pos_z + (b.position.pos_z - a.position.pos_z) * t,
        },
        velocity: {
            vel_x: a.velocity.vel_x + (b.velocity.vel_x - a.velocity.vel_x) * t,
            vel_y: a.velocity.vel_y + (b.velocity.vel_y - a.velocity.vel_y) * t,
            vel_z: a.velocity.vel_z + (b.velocity.vel_z - a.velocity.vel_z) * t,
        },
        orientation: {
            q_1: a.orientation.q_1 + (b.orientation.q_1 - a.orientation.q_1) * t,
            q_2: a.orientation.q_2 + (b.orientation.q_2 - a.orientation.q_2) * t,
            q_3: a.orientation.q_3 + (b.orientation.q_3 - a.orientation.q_3) * t,
            q_4: a.orientation.q_4 + (b.orientation.q_4 - a.orientation.q_4) * t,
        },
    };
};

/**
 * Convert a VisualizationStateEntry into the ElementPositionData format
 * that updateEntityFromECEF expects.
 */
const stateToPositionData = (
    elementId: string,
    elementName: string,
    state: VisualizationStateEntry,
): ElementPositionData => ({
    elementId,
    elementName,
    timestamp: state.timestamp,
    position: { x: state.position.pos_x, y: state.position.pos_y, z: state.position.pos_z },
    velocity: { x: state.velocity.vel_x, y: state.velocity.vel_y, z: state.velocity.vel_z },
    quaternion: {
        q1: state.orientation.q_1,
        q2: state.orientation.q_2,
        q3: state.orientation.q_3,
        q4: state.orientation.q_4,
    },
});

/**
 * Convert a VisualizationStateEntry into ECEFState for history cache.
 */
const stateToECEF = (state: VisualizationStateEntry): ECEFState => ({
    pos_x: state.position.pos_x,
    pos_y: state.position.pos_y,
    pos_z: state.position.pos_z,
    vel_x: state.velocity.vel_x,
    vel_y: state.velocity.vel_y,
    vel_z: state.velocity.vel_z,
    q_1: state.orientation.q_1,
    q_2: state.orientation.q_2,
    q_3: state.orientation.q_3,
    q_4: state.orientation.q_4,
    timestamp: state.timestamp,
});

// ═══════════════════════════════════════════════════════════════════════

const useSummaryReplay = () => {
    const {
        viewer,
        visualizationData,
        replayState,
        setReplayState,
        elementMap,
        setElementMap,
        visualizationSettings,
        selectedElement,
        setSelectedElement,
    } = useGenericSimulator();

    // Refs for animation loop access
    const preparedRef = useRef<PreparedElement[]>([]);
    const replayRef = useRef<ReplayState>(replayState);
    replayRef.current = replayState;

    const elementMapRef = useRef(elementMap);
    elementMapRef.current = elementMap;

    const visSettingsRef = useRef(visualizationSettings);
    visSettingsRef.current = visualizationSettings;

    const selectedElementRef = useRef(selectedElement);
    selectedElementRef.current = selectedElement;

    const setSelectedElementRef = useRef(setSelectedElement);
    setSelectedElementRef.current = setSelectedElement;

    const rafIdRef = useRef<number | null>(null);
    const lastFrameTimeRef = useRef<number>(0);

    // ─── Step 1: Pre-process visualization data ─────────────────────
    useEffect(() => {
        if (!visualizationData || !visualizationData.elements?.length) return;

        // Build element map from visualization data elements
        const elMap: ElementMap = { ...elementMapRef.current };
        const prepared: PreparedElement[] = [];
        let globalMin = Infinity;
        let globalMax = -Infinity;

        for (const elem of visualizationData.elements) {
            // Sort states ascending by rel_time_ms (API returns descending)
            const sorted = [...elem.elementStateData].sort(
                (a, b) => a.rel_time_ms - b.rel_time_ms,
            );

            if (sorted.length === 0) continue;

            globalMin = Math.min(globalMin, sorted[0].rel_time_ms);
            globalMax = Math.max(globalMax, sorted[sorted.length - 1].rel_time_ms);

            prepared.push({
                elementId: elem.elementId,
                elementName: elem.elementName,
                states: sorted,
                cursor: 0,
            });

            // Register in elementMap if not already there
            if (!elMap[elem.elementId]) {
                const firstState = sorted[0];
                const isFixed = firstState.velocity.vel_x === 0
                    && firstState.velocity.vel_y === 0
                    && firstState.velocity.vel_z === 0;
                const nameInferred = inferElementTypeFromName(elem.elementName);
                const autoType = isFixed
                    ? 'groundstation'
                    : nameInferred !== 'unknown'
                        ? nameInferred
                        : 'satellite';

                elMap[elem.elementId] = {
                    elementId: elem.elementId,
                    elementName: elem.elementName,
                    elementType: autoType,
                };
            }
        }

        preparedRef.current = prepared;
        setElementMap(elMap);
        elementMapRef.current = elMap;

        // Initialize replay state
        setReplayState({
            status: 'idle',
            currentTimeMs: globalMin,
            startTimeMs: globalMin,
            endTimeMs: globalMax,
            speed: 1,
        });
    }, [visualizationData]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Step 2: Render a single frame at a given time ──────────────
    const renderFrame = useCallback((timeMs: number) => {
        if (!isViewerReady(viewer)) return;

        const selEl = selectedElementRef.current;

        for (const elem of preparedRef.current) {
            const idx = findFrameIndex(elem.states, timeMs);
            if (idx < 0) continue; // Before this element's first data point

            let state: VisualizationStateEntry;

            // Interpolate if there's a next frame
            if (idx < elem.states.length - 1) {
                state = interpolateState(elem.states[idx], elem.states[idx + 1], timeMs);
            } else {
                state = elem.states[idx];
            }

            const posData = stateToPositionData(elem.elementId, elem.elementName, state);
            updateEntityFromECEF(viewer, posData, elementMapRef.current, visSettingsRef.current);

            // Also append to ECEF history so VisualizationControls can read it
            appendEcefHistory(elem.elementId, stateToECEF(state));

            // Update the selected element's info panel if this element is selected
            if (selEl && selEl.elementId === elem.elementId) {
                const pos = { x: state.position.pos_x, y: state.position.pos_y, z: state.position.pos_z };
                const vel = { x: state.velocity.vel_x, y: state.velocity.vel_y, z: state.velocity.vel_z };
                const quat = { q1: state.orientation.q_1, q2: state.orientation.q_2, q3: state.orientation.q_3, q4: state.orientation.q_4 };
                const altitude = computeAltitudeKm(pos);
                const speed = computeSpeedMs(vel);
                const euler = computeLVLHEulerAngles(pos, vel, quat);
                const updated: SelectedElementInfo = {
                    elementId: selEl.elementId,
                    elementName: selEl.elementName,
                    elementType: selEl.elementType,
                    position: pos,
                    velocity: vel,
                    quaternion: quat,
                    altitude,
                    speed,
                };
                if (euler) updated.eulerAngles = euler;
                setSelectedElementRef.current(updated);
            }
        }

        // Update geometric comm lines after all positions are set for this frame
        updateGeometricCommLines(viewer, elementMapRef.current, visSettingsRef.current);

        throttledRequestRender(viewer);
    }, [viewer]);

    // ─── Step 3: Animation loop ─────────────────────────────────────
    const tick = useCallback((now: number) => {
        const state = replayRef.current;
        if (state.status !== 'playing') return;

        const dt = lastFrameTimeRef.current ? (now - lastFrameTimeRef.current) : 0;
        lastFrameTimeRef.current = now;

        // Advance replay time by dt * speed (dt is in ms from rAF)
        const advance = dt * state.speed;
        const newTime = Math.min(state.currentTimeMs + advance, state.endTimeMs);

        renderFrame(newTime);

        setReplayState(prev => ({ ...prev, currentTimeMs: newTime }));

        if (newTime >= state.endTimeMs) {
            // Reached end — pause
            setReplayState(prev => ({ ...prev, status: 'paused', currentTimeMs: prev.endTimeMs }));
            lastFrameTimeRef.current = 0;
            return;
        }

        rafIdRef.current = requestAnimationFrame(tick);
    }, [renderFrame, setReplayState]);

    // ─── Step 4: Start/stop animation loop based on replay status ───
    useEffect(() => {
        if (replayState.status === 'playing') {
            lastFrameTimeRef.current = 0;
            rafIdRef.current = requestAnimationFrame(tick);
        } else {
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
            lastFrameTimeRef.current = 0;
        }

        return () => {
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, [replayState.status, tick]);

    // ─── Controls ───────────────────────────────────────────────────
    const play = useCallback(() => {
        const s = replayRef.current;
        // If at end, restart from beginning
        if (s.currentTimeMs >= s.endTimeMs) {
            setReplayState(prev => ({ ...prev, status: 'playing', currentTimeMs: prev.startTimeMs }));
        } else {
            setReplayState(prev => ({ ...prev, status: 'playing' }));
        }
    }, [setReplayState]);

    const pause = useCallback(() => {
        setReplayState(prev => ({ ...prev, status: 'paused' }));
    }, [setReplayState]);

    const seek = useCallback((timeMs: number) => {
        const clamped = Math.max(replayRef.current.startTimeMs, Math.min(timeMs, replayRef.current.endTimeMs));
        // Pause playback so the rAF loop doesn't overwrite the seeked position
        if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
        lastFrameTimeRef.current = 0;
        setReplayState(prev => ({ ...prev, currentTimeMs: clamped, status: 'paused' }));
        renderFrame(clamped);
    }, [setReplayState, renderFrame]);

    const setSpeed = useCallback((speed: number) => {
        setReplayState(prev => ({ ...prev, speed }));
    }, [setReplayState]);

    // ─── Auto-play on data load ─────────────────────────────────────
    useEffect(() => {
        if (
            visualizationData?.elements?.length &&
            replayState.status === 'idle' &&
            replayState.endTimeMs > replayState.startTimeMs &&
            isViewerReady(viewer)
        ) {
            // Render first frame then auto-play
            renderFrame(replayState.startTimeMs);
            setTimeout(() => {
                setReplayState(prev => ({ ...prev, status: 'playing' }));
            }, 500);
        }
    }, [replayState.status, replayState.endTimeMs, viewer]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Cleanup ────────────────────────────────────────────────────
    useEffect(() => {
        return () => {
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
            }
            cleanupCesiumPlot(viewer);
        };
    }, [viewer]);

    return { play, pause, seek, setSpeed };
};

export default useSummaryReplay;
